import React, { useEffect, useRef, useState } from 'react'
import { io } from 'socket.io-client'
import { ArrowDownTrayIcon, ClockIcon } from '@heroicons/react/24/solid'

export default function TradeHistoryPage() {
  const [section, setSection] = useState('fetch')
  const [exchanges, setExchanges] = useState(['binance'])
  const [exchange, setExchange] = useState('binance')
  const [apiKey, setApiKey] = useState('')
  const [secret, setSecret] = useState('')
  const [sinceDate, setSinceDate] = useState('2024-01-01T00:00:00Z')
  const [untilDate, setUntilDate] = useState('2024-02-01T00:00:00Z')
  const [loading, setLoading] = useState(false)
  const [progress, setProgress] = useState([])
  const [pairsMode, setPairsMode] = useState('all')
  const [pairsText, setPairsText] = useState('')
  const socketRef = useRef(null)
  const logRef = useRef(null)

  useEffect(() => {
    fetch('/api/exchanges')
      .then(r => r.json())
      .then(data => {
        if (data?.ok && Array.isArray(data.exchanges) && data.exchanges.length) {
          setExchanges(data.exchanges)
          setExchange(data.exchanges.includes('binance') ? 'binance' : data.exchanges[0])
        }
      })
      .catch(() => {})
  }, [])

  useEffect(() => {
    const socket = io('/', { path: '/socket.io' })
    socketRef.current = socket
    socket.on('tradeProgress', msg => setProgress(prev => [...prev, msg]))
    return () => {
      socket.disconnect()
      socketRef.current = null
    }
  }, [])

  useEffect(() => {
    if (logRef.current) logRef.current.scrollTop = logRef.current.scrollHeight
  }, [progress])

  const handleSubmit = async e => {
    e.preventDefault()
    setLoading(true)
    setProgress([])

    const socketId = socketRef.current?.id
    if (!socketId) {
      setProgress(p => [...p, '❌ Socket not connected.'])
      setLoading(false)
      return
    }

    let pairs = []
    if (pairsMode === 'list') {
      pairs = pairsText.split(/[\n,]+/).map(s => s.trim()).filter(Boolean)
    }

    try {
      const res = await fetch('/api/fetch-trades', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ exchangeId: exchange, apiKey, secret, sinceDate, untilDate, socketId, pairs }),
      })
      const result = await res.json()
      if (result.ok) {
        setProgress(p => [...p, '✅ Trade history saved successfully'])
        if (result?.files?.length) {
          setProgress(p => [...p, `Saved files: ${result.files.length}`])
        }
      } else setProgress(p => [...p, `❌ Error: ${result.error}`])
    } catch (err) {
      setProgress(p => [...p, `❌ Network error: ${err.message}`])
    } finally {
      setLoading(false)
    }
  }

  const clearLog = () => setProgress([])

  const localSince = sinceDate.replace('Z', '')
  const localUntil = untilDate.replace('Z', '')

  const lineClass = msg => {
    if (msg.startsWith('✅') || msg.startsWith('Saved')) return 'text-green-400'
    if (msg.includes('No trades')) return 'text-yellow-400'
    if (msg.startsWith('❌') || msg.toLowerCase().includes('error')) return 'text-red-500'
    return 'text-gray-300'
  }

  return (
    <div className="flex h-screen w-full bg-[#141414] text-gray-200 overflow-hidden">
      {/* === Sidebar === */}
      <aside className="fixed left-0 top-0 bottom-0 w-60 bg-[#0d0d0d] border-r border-[#222] p-5 flex flex-col">
        <div className="text-lg font-semibold mb-8 text-white tracking-wide">
          Trade Tools
        </div>

        <nav className="flex flex-col gap-1">
          {[
            { key: 'fetch', label: 'Fetch Trade History', icon: ClockIcon },
            { key: 'backups', label: 'Backups', icon: ArrowDownTrayIcon },
          ].map(({ key, label, icon: Icon }) => (
            <button
              key={key}
              onClick={() => setSection(key)}
              className={`flex items-center gap-2 text-left px-3 py-2 rounded-md transition ${
                section === key
                  ? 'bg-[#1b1b1b] text-white'
                  : 'hover:bg-[#161616] text-gray-400'
              }`}
            >
              <Icon className="w-4 h-4" />
              <span>{label}</span>
            </button>
          ))}
        </nav>

        <div className="mt-6 text-xs text-gray-500">More tools coming soon</div>
      </aside>

      {/* === Main Content === */}
      <main
        className="flex flex-col flex-1 ml-60 h-full overflow-y-auto px-10 py-10"
        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
      >
        <style>{`main::-webkit-scrollbar { display: none; }`}</style>

        {section === 'fetch' && (
          <section className="w-full max-w-full overflow-x-hidden">
            

            <form onSubmit={handleSubmit} className="space-y-6 bg-[#1a1a1a] p-6 rounded-lg border border-[#2a2a2a] shadow-sm">
              {/* Exchange */}
              <div>
                <label className="block text-sm text-gray-400 mb-1">Exchange</label>
                <select
                  value={exchange}
                  onChange={e => setExchange(e.target.value)}
                  className="w-full bg-[#111] text-gray-200 border border-[#2a2a2a] rounded-md px-3 py-2 focus:border-green-500 outline-none"
                >
                  {exchanges.map(ex => (
                    <option key={ex}>{ex}</option>
                  ))}
                </select>
              </div>

              {/* Keys */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-gray-400 mb-1">API Key</label>
                  <input
                    type="text"
                    value={apiKey}
                    onChange={e => setApiKey(e.target.value)}
                    className="w-full bg-[#111] text-gray-200 border border-[#2a2a2a] rounded-md px-3 py-2 focus:border-green-500 outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Secret</label>
                  <input
                    type="password"
                    value={secret}
                    onChange={e => setSecret(e.target.value)}
                    className="w-full bg-[#111] text-gray-200 border border-[#2a2a2a] rounded-md px-3 py-2 focus:border-green-500 outline-none"
                  />
                </div>
              </div>

              {/* Date Range */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Since</label>
                  <input
                    type="datetime-local"
                    value={localSince}
                    onChange={e => setSinceDate(new Date(e.target.value).toISOString())}
                    className="w-full bg-[#111] text-gray-200 border border-[#2a2a2a] rounded-md px-3 py-2 focus:border-green-500 outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Until</label>
                  <input
                    type="datetime-local"
                    value={localUntil}
                    onChange={e => setUntilDate(new Date(e.target.value).toISOString())}
                    className="w-full bg-[#111] text-gray-200 border border-[#2a2a2a] rounded-md px-3 py-2 focus:border-green-500 outline-none"
                  />
                </div>
              </div>

              {/* Pairs */}
              <div>
                <label className="block text-sm text-gray-400 mb-1">Pairs</label>
                <div className="flex items-center gap-6 text-gray-300 text-sm">
                  <label className="flex items-center gap-2">
                    <input
                      type="radio"
                      checked={pairsMode === 'all'}
                      onChange={() => setPairsMode('all')}
                      className="accent-green-500"
                    />
                    All tradable pairs
                  </label>
                  <label className="flex items-center gap-2">
                    <input
                      type="radio"
                      checked={pairsMode === 'list'}
                      onChange={() => setPairsMode('list')}
                      className="accent-green-500"
                    />
                    Custom list
                  </label>
                </div>

                {pairsMode === 'list' && (
                  <textarea
                    rows={3}
                    placeholder="BTC/USDT, ETH/USDT, ADA/USDT …"
                    value={pairsText}
                    onChange={e => setPairsText(e.target.value)}
                    className="w-full mt-2 bg-[#111] text-gray-200 border border-[#2a2a2a] rounded-md px-3 py-2 focus:border-green-500 outline-none"
                  />
                )}
              </div>

              {/* Buttons */}
              <div className="flex gap-3">
                <button
                  type="submit"
                  disabled={loading}
                  className="bg-[#111] text-green-400 border border-green-500 px-4 py-2 rounded-md hover:bg-[#1d1d1d] transition disabled:opacity-50"
                >
                  {loading ? 'Fetching…' : 'Fetch Trades'}
                </button>
                <button
                  type="button"
                  disabled={loading}
                  onClick={clearLog}
                  className="border border-[#2a2a2a] text-gray-300 px-4 py-2 rounded-md hover:bg-[#1d1d1d] transition"
                >
                  Clear log
                </button>
              </div>
            </form>

            {/* Log Output */}
            <div
              ref={logRef}
              className="mt-6 bg-[#111] border border-[#2a2a2a] rounded-md p-3 max-h-80 overflow-y-auto text-sm font-mono"
            >
              {progress.length === 0 ? (
                <div className="text-gray-400">
                  <b>🆕 New feature:</b> Fetch your complete trading history from any ccxt-supported exchange for a chosen period.
                  <br />
                  Useful to rebuild lost Gunbot history or export clean data for tax reports.
                </div>
              ) : (
                progress.map((msg, i) => (
                  <div key={i} className={`${lineClass(msg)} mb-1`}>
                    {msg}
                  </div>
                ))
              )}
            </div>
          </section>
        )}

        {section === 'backups' && (
          <section className="w-full max-w-full overflow-x-hidden">
            

            <div className="bg-[#1a1a1a] border border-[#2a2a2a] rounded-lg p-6 shadow-sm">
              <p className="text-gray-400 mb-3">
                Manage Gunbot Monitor backups. Options like scheduling, restore, and cloud sync coming soon.
              </p>
              <div className="flex gap-3">
                <button className="bg-[#111] text-green-400 border border-green-500 px-4 py-2 rounded-md opacity-60 cursor-not-allowed">
                  Create Backup
                </button>
                <button className="border border-[#2a2a2a] text-gray-300 px-4 py-2 rounded-md opacity-60 cursor-not-allowed">
                  Restore Backup
                </button>
              </div>
            </div>
          </section>
        )}
      </main>
    </div>
  )
}
